import { useMemo } from 'react';

import { AxiosError } from 'axios';
import { useFormContext, useWatch } from 'react-hook-form';
import useSWR from 'swr';

import { Modal } from '@quick/components/Modal';
import type { IGeoDirection } from '@quick/go-api-types/geo/direction';

import { useAppTranslation } from '@/app/i18n/client';
import { ServiceRouteMap } from '@/components/address';
import { ErrorMessage, InlineSpinner } from '@/components/core';
import { idToCountry } from '@/layers/core/domain/countryMap';
import { axios_demand } from '@/layers/demand/data/fetchers/instances';

import type { IForm } from '../types';

export interface IDirection {
  address: string;
  city_id: string;
  country_id: string;
}

export interface IViewAddressModal {
  onClose: () => void;
  country: (typeof idToCountry)[keyof typeof idToCountry];
  useFirstAddress?: boolean;
}

export function ViewAddressesModal({
  onClose,
  country,
  useFirstAddress,
}: IViewAddressModal) {
  const { t } = useAppTranslation(['common', 'demand']);

  const { getValues, control } = useFormContext<IForm>();
  const values = useWatch({ control });
  const addresses = { ...values, ...getValues() }.stops;

  const { data: coordinatesData, error } = useSWR<
    Pick<IGeoDirection, 'latitude' | 'longitude'>[],
    AxiosError<{ detail: unknown }>,
    IForm['stops']
  >(addresses, (data: IForm['stops']) =>
    Promise.all(
      data
        .slice(0, useFirstAddress ? 1 : undefined)
        .map(async ({ address, city_id, latitude, longitude }) => {
          if (latitude && longitude)
            return { latitude: Number(latitude), longitude: Number(longitude) };
          const point = {
            address: address,
            city_id: city_id,
            country_id: country,
          };
          const { data } = await axios_demand.post<IGeoDirection>(
            `/geo/direction`,
            point
          );
          return data;
        })
    )
  );

  const geoCoordinates = useMemo(
    () =>
      coordinatesData
        ?.map(({ latitude, longitude }) => {
          if (latitude && longitude) {
            return {
              lat: latitude,
              lng: longitude,
            };
          }
        })
        .filter((c): c is { lat: number; lng: number } => !!c),
    [coordinatesData]
  );

  return (
    <Modal
      className="!z-75 absolute left-0 top-0 h-screen w-screen"
      events={{
        onClose,
      }}
    >
      <div className="h-full w-[30rem] overflow-y-auto px-4">
        <div className="mx-auto my-4 w-fit">
          <h2 className="text-center text-lg dark:text-white">
            {t('demand:addresses.map')}
          </h2>
        </div>
        {geoCoordinates ? (
          <ServiceRouteMap points={geoCoordinates} />
        ) : error ? (
          <ErrorMessage
            title="Error"
            data={error?.response?.data?.detail ?? error?.response?.data}
          />
        ) : (
          <div className="flex w-full items-center justify-center py-4">
            <InlineSpinner />
          </div>
        )}
      </div>
    </Modal>
  );
}
